import { useState } from "react";
import Background from "../../../components/Background";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Topnav from "../../../components/Topnav";
import CopyRightTextMain from "../../../components/CopyRightTextMain";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateFields } from "../../../redux/profileSlice";
import { useEditableProfile } from "../../../hooks/useEditableProfile";
import { profileOptionButtonStyles } from "./styles";

const canonicalToLegacyLgbtq = {
  yes: "Yes",
  no: "No",
  prefer_not_to_say: "prefer not to say",
};
const legacyToCanonicalLgbtq = Object.fromEntries(
  Object.entries(canonicalToLegacyLgbtq).map(([key, value]) => [value, key]),
);

const EditLGBTQDetails = () => {
  // Router state when opened from the Profile UI, the stored profile on a
  // reload or a pasted link (ECH-387). `RequireProfileLoaded` guarantees one
  // of them exists before this component mounts, so the initialisers below
  // are safe to dereference.
  const { profile: localProfile } = useEditableProfile();
  const demographics = localProfile?.demographics ?? {};
  const [selectedOption, setSelectedOption] = useState(
    canonicalToLegacyLgbtq[demographics.lgbtqIdentities?.[0]] ??
      demographics.lgbtqIdentities?.[0],
  );
  const [isChecked, setIsChecked] = useState(demographics.lgbtqVisible);
  const hasAnswer = Boolean(selectedOption);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSelect = (option) => {
    const value = option;
    setSelectedOption(option);
    dispatch(
      updateFields({
        field: "demographics.lgbtqIdentities",
        value: [legacyToCanonicalLgbtq[value] ?? value],
      }),
    );
  };

  const handleCheckboxChange = (event) => {
    if (!hasAnswer) return;
    setIsChecked(event.target.checked);
    dispatch(
      updateFields({
        field: "demographics.lgbtqVisible",
        value: event.target.checked,
      }),
    );
  };

  if (!localProfile) {
    return <div>No profile data found.</div>;
  }

  return (
    <Background>
      <Topnav />
      <Box className="selection-box">
        <Typography
          className="text-primary"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
              lg: "26px",
              xl: "32px",
            },
            marginBottom: "5px",
          }}
        >
          Do you identify with the 2SLGBTQ+ community?
        </Typography>
        <Typography
          variant="body"
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            color: "#152134",
          }}
        >
          To better understand our community and ensure inclusivity, we would
          like to know if you identify with the 2SLGBTQ+ community. Your
          information is confidential and will only be used for demographic
          measurement purposes.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <Button
            className="hover-tag-color"
            style={{
              ...profileOptionButtonStyles,
              padding: "10px 20px",
              borderRadius: "100px",
              fontSize: "16px",
              minWidth: "48%",
              backgroundColor: selectedOption === "Yes" ? "#7BB9D5" : "white",
              fontWeight: selectedOption === "Yes" ? 600 : 400,
            }}
            onClick={() => handleSelect("Yes")}
          >
            Yes
          </Button>
          <Button
            className="hover-tag-color"
            style={{
              ...profileOptionButtonStyles,
              padding: "10px 20px",
              borderRadius: "100px",
              fontSize: "16px",
              minWidth: "48%",
              backgroundColor: selectedOption === "No" ? "#7BB9D5" : "white",
              fontWeight: selectedOption === "No" ? 600 : 400,
            }}
            onClick={() => handleSelect("No")}
          >
            No
          </Button>
        </Box>
        <FormControlLabel
          sx={{
            display: "flex",
            justifyContent: "start",
            textAlign: "left",
            marginLeft: 0,
            marginTop: "12px",
          }}
          control={
            <Checkbox
              color="primary"
              checked={hasAnswer && Boolean(isChecked)}
              disabled={!hasAnswer}
              onChange={handleCheckboxChange}
              sx={{
                margin: 0,
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "&.Mui-checked": {
                  color: "#7BB9D5",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{ textAlign: "left", paddingLeft: "8px" }}
              variant="body5"
              className="text-xs text-text-color"
            >
              Display my 2SLGBTQ+ identification on my profile
            </Typography>
          }
        />
        <Button
          variant="contained"
          className="Send-button"
          sx={{
            backgroundColor: selectedOption ? "#2A4876" : "#AAB6C8", // Change color based on input values
            cursor: selectedOption ? "pointer" : "not-allowed", // Disable hover effect when fields are not filled
            color: "#fff",
            marginTop: "20px !important",
            width: "370px",
          }}
          disabled={!selectedOption}
          onClick={() => {
            navigate("/profile/edit-profile");
          }}
        >
          Save
        </Button>
        <Typography
          variant="body2"
          style={{
            cursor: "pointer",
          }}
          sx={{
            color: "#152134",
            fontSize: "14px",
            marginTop: {
              xs: "10px",
              sm: "10px",
              md: "10px",
              lg: "20px",
              xl: "20px",
            },
          }}
          onClick={() => {
            handleSelect("prefer not to say");
            navigate("/profile/edit-profile");
          }}
        >
          Prefer not to say
        </Typography>
      </Box>
      <CopyRightTextMain />
    </Background>
  );
};

export default EditLGBTQDetails;
